// controllers/usb.printer.js

let device = null;
let interfaceNumber = null;
let endpointOut = null;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function getOutEndpointFromDevice(dev) {
  const cfg = dev.configuration;
  for (const iface of cfg.interfaces) {
    for (const alt of iface.alternates) {
      const ep = alt.endpoints?.find((e) => e.direction === "out");
      if (ep) {
        return {
          interfaceNumber: iface.interfaceNumber,
          endpointOut: ep.endpointNumber,
        };
      }
    }
  }
  return null;
}

/**
 * Connect and prepare printer for WebUSB bulk OUT transfers.
 * Call from a user gesture (button click).
 */
export async function connectUSBPrinter() {
  try {
    device = await navigator.usb.requestDevice({
      // Many thermal printers expose class 7 (printer), but some expose vendor-specific.
      // If your printer doesn't show, remove filters or add vendorId/productId.
      filters: [{ classCode: 7 }],
    });

    await device.open();

    if (!device.configuration) {
      await device.selectConfiguration(1);
    }

    const found = getOutEndpointFromDevice(device);
    if (!found) {
      throw new Error("USB printer OUT endpoint not found. Try removing filters or check interfaces.");
    }

    interfaceNumber = found.interfaceNumber;
    endpointOut = found.endpointOut;

    await device.claimInterface(interfaceNumber);

    return true;
  } catch (err) {
    if (err?.name === "NotFoundError") return false; // user cancelled picker
    throw err;
  }
}

/**
 * Characteristic-like writer so your existing code can stay the same:
 * await characteristic.writeValue(Uint8Array)
 */
export function getUSBWriter() {
  if (!device || endpointOut == null) return null;

  return {
    async writeValue(data) {
      // Ensure Uint8Array
      const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);

      // Chunking is important for stability (printers vary).
      const CHUNK = 4096;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        const chunk = bytes.slice(i, i + CHUNK);
        await device.transferOut(endpointOut, chunk);
        // small delay helps some printers
        await sleep(2);
      }
    },
  };
}

export async function disconnectUSBPrinter() {
  try {
    if (device) {
      if (interfaceNumber != null) {
        await device.releaseInterface(interfaceNumber).catch(() => {});
      }
      await device.close().catch(() => {});
    }
  } finally {
    device = null;
    interfaceNumber = null;
    endpointOut = null;
  }
}
